import React, { useState } from 'react';
import { format } from 'date-fns';
import { CheckCircle, User, CreditCard, Shield, Settings, Search } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import adminService from '../../../utils/adminService';
import Icon from '../../../components/AppIcon';


const OnboardingStatus = ({ onboarding, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const filteredOnboarding = onboarding?.filter(item =>
    item?.clients?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item?.clients?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item?.stage?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getStageIcon = (stage) => {
    switch (stage) {
      case 'welcome':
        return User;
      case 'profile':
        return User;
      case 'billing':
        return CreditCard;
      case 'verification':
        return Shield;
      case 'completed':
        return CheckCircle;
      default:
        return Settings;
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'verification':
        return 'bg-blue-100 text-blue-800';
      case 'billing':
        return 'bg-yellow-100 text-yellow-800';
      case 'profile':
        return 'bg-purple-100 text-purple-800';
      case 'welcome':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressPercentage = (stage, completedSteps) => {
    const totalSteps = 4;
    const stageOrder = ['welcome', 'profile', 'billing', 'verification', 'completed'];
    const currentStageIndex = stageOrder.indexOf(stage);
    
    if (stage === 'completed') return 100;
    if (currentStageIndex === -1) return 0;
    
    const completedCount = Array.isArray(completedSteps) ? completedSteps.length : 0;
    return Math.round((completedCount / totalSteps) * 100);
  };

  const handleUpdateStage = async (clientId, newStage) => {
    try {
      setLoading(true);
      const result = await adminService.updateOnboardingStage(clientId, newStage);
      
      if (result.success) {
        onRefresh();
      } else {
        alert(result.error || 'Failed to update onboarding stage');
      }
    } catch (error) {
      alert('Failed to update onboarding stage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Client Onboarding</h2>
          <Button variant="outline" onClick={onRefresh}>
            Refresh
          </Button>
        </div>
        
        <div className="mt-4">
          <div className="relative max-w-lg">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Stage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Progress
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Started
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredOnboarding.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No onboarding records found
                </td>
              </tr>
            ) : (
              filteredOnboarding.map((item) => {
                const Icon = getStageIcon(item?.stage);
                const progress = getProgressPercentage(item?.stage, item?.completed_steps);
                
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                            <span className="text-white font-medium">
                              {item?.clients?.company_name?.charAt(0)?.toUpperCase() || 'C'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {item?.clients?.company_name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {item?.clients?.email || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Icon className="w-4 h-4 mr-2 text-gray-400" />
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          getStageColor(item?.stage)
                        }`}>
                          {item?.stage?.charAt(0)?.toUpperCase() + item?.stage?.slice(1) || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {progress}%
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {Array.isArray(item?.completed_steps) ? item.completed_steps.length : 0} of 4 steps completed
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item?.started_at ? format(new Date(item.started_at), 'MMM d, yyyy') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {item?.stage !== 'completed' && (
                          <select
                            value={item?.stage || ''}
                            onChange={(e) => handleUpdateStage(item.client_id, e.target.value)}
                            disabled={loading}
                            className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="welcome">Welcome</option>
                            <option value="profile">Profile</option>
                            <option value="billing">Billing</option>
                            <option value="verification">Verification</option>
                            <option value="completed">Completed</option>
                          </select>
                        )}
                        {item?.stage === 'completed' && (
                          <span className="text-green-600 flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Complete
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {filteredOnboarding.length > 10 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">1</span> to{' '}
              <span className="font-medium">{Math.min(10, filteredOnboarding.length)}</span> of{' '}
              <span className="font-medium">{filteredOnboarding.length}</span> results
            </p>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm">
                Previous
              </Button>
              <Button variant="outline" size="sm">
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingStatus;